// Utilidad compartida para generar etiquetas QR de piezas.
// - SVG vectorial (para pegar en el arte de la plancha) con el identificador impreso debajo.
// - PNG alto contraste (para impresora térmica Bluetooth de stickers) con el identificador debajo.
// El QR codifica el payload {jobId, pieceCode} para que el escáner del inventario lo lea igual.
import QRCode from 'qrcode';

const escapeXml = (str) => String(str).replace(/[<>&'"]/g, (c) => (
    { '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[c]
));

const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// Descarga un QR en SVG (vectorial) con el texto `label` debajo.
export async function downloadQrSvg(payload, label, filename) {
    const qrSvg = await QRCode.toString(payload, { type: 'svg', margin: 1 });
    // El QR trae su propio viewBox; lo incrustamos como <svg> anidado escalado a un recuadro fijo.
    const inner = qrSvg.replace('<svg ', '<svg x="30" y="20" width="300" height="300" ');
    const W = 360, H = 360;
    const composite =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">` +
        `<rect width="${W}" height="${H}" fill="#ffffff"/>` +
        inner +
        `<text x="${W / 2}" y="345" font-family="Arial, sans-serif" font-size="22" font-weight="bold" text-anchor="middle" fill="#000000">${escapeXml(label)}</text>` +
        `</svg>`;
    triggerDownload(new Blob([composite], { type: 'image/svg+xml' }), filename);
}

// Descarga un QR en PNG de ALTA RESOLUCIÓN para incrustar en el arte de la plancha.
// PNG (no SVG): el SVG, al reducirlo, fusiona los módulos en una sola masa negra. El
// PNG a 1000px con imageSmoothing desactivado mantiene los módulos nítidos al escalar.
export async function downloadQrArtPng(payload, label, filename) {
    const qrSize = 1000;
    const qrDataUrl = await QRCode.toDataURL(payload, { width: qrSize, margin: 1, errorCorrectionLevel: 'M' });
    const W = qrSize + 80;
    const hasLabel = !!(label && String(label).trim());
    const H = W + (hasLabel ? 80 : 0) - (hasLabel ? 0 : 40);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    const qrImg = new Image();
    await new Promise((resolve, reject) => {
        qrImg.onload = resolve;
        qrImg.onerror = reject;
        qrImg.src = qrDataUrl;
    });
    ctx.imageSmoothingEnabled = false; // bordes nítidos: nada de anti-aliasing que junte módulos
    ctx.drawImage(qrImg, (W - qrSize) / 2, 40, qrSize, qrSize);

    if (hasLabel) {
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        let fontSize = 56;
        ctx.font = `bold ${fontSize}px Arial, sans-serif`;
        while (ctx.measureText(label).width > W - 60 && fontSize > 18) {
            fontSize -= 2;
            ctx.font = `bold ${fontSize}px Arial, sans-serif`;
        }
        ctx.fillText(label, W / 2, qrSize + 110);
    }

    await new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (blob) triggerDownload(blob, filename);
            resolve();
        }, 'image/png');
    });
}

// Descarga un QR en PNG (alto contraste) con el texto `label` debajo, listo para sticker térmico.
export async function downloadQrPng(payload, label, filename) {
    const qrDataUrl = await QRCode.toDataURL(payload, { width: 460, margin: 1, errorCorrectionLevel: 'M' });
    const W = 500, H = 580;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);

    const qrImg = new Image();
    await new Promise((resolve, reject) => {
        qrImg.onload = resolve;
        qrImg.onerror = reject;
        qrImg.src = qrDataUrl;
    });
    const qrSize = 440;
    ctx.drawImage(qrImg, (W - qrSize) / 2, 20, qrSize, qrSize);

    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    // Reduce la fuente hasta que el identificador entre en el ancho del lienzo.
    let fontSize = 44;
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    while (ctx.measureText(label).width > W - 30 && fontSize > 14) {
        fontSize -= 2;
        ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    }
    ctx.fillText(label, W / 2, 525);

    await new Promise((resolve) => {
        canvas.toBlob((blob) => {
            if (blob) triggerDownload(blob, filename);
            resolve();
        }, 'image/png');
    });
}
